import { CrossIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { NumberField } from "../../../components/NumberField";
import { forcedFieldBorderClasses } from "../../../components/ui/force-state";
import { cn } from "../../../utils/cn";

import { IconButton } from "./IconButton";
import { MultiplierField } from "./MultiplierField";

export interface TrancheInputProps {
    /** Threshold amount for this tranche (USDv balance or days held). `null` =
     *  empty (not yet typed) — the field renders blank. */
    amount: number | null;
    /** Reward multiplier applied at/above this threshold. `null` = empty. */
    multiplier: number | null;
    /** aria-label for the amount field — the domain unit's label. */
    amountLabel: string;
    /**
     * Trailing adornment for the amount field — the unit decoration shown at
     * the inline-end (e.g. a "days" label or a "USDv" token chip). A slot, so
     * this row never imports a domain unit.
     */
    amountAdornment: React.ReactNode;
    onAmountChange: (amount: number | null) => void;
    onMultiplierChange: (multiplier: number | null) => void;
    onRemove: () => void;
    /** Sim-only cursor key placed on the amount NumberField container. */
    amountCursorKey?: string;
    /** Sim-only — force the amount field's hover visual. */
    amountForceHover?: boolean;
    /** Sim-only — force the amount field's focus visual. */
    amountForceFocus?: boolean;
    /** Sim-only cursor key placed on the multiplier field's input. */
    multiplierCursorKey?: string;
    /** Sim-only — force the multiplier field wrapper's hover visual. */
    multiplierForceHover?: boolean;
    /** Sim-only — force the multiplier field wrapper's focus visual. */
    multiplierForceFocus?: boolean;
}

/**
 * TrancheInput — ONE tranche row: a threshold-amount `NumberField` (with a
 * unit adornment slot), a `MultiplierField` (× Multiplier), and a remove ✕
 * button.
 *
 * Generic Component: the unit (days vs USDv) enters as the `amountAdornment`
 * slot; the caller owns parse/serialize and the list. Pure controlled View.
 */
export function TrancheInput({
    amount,
    multiplier,
    amountLabel,
    amountAdornment,
    onAmountChange,
    onMultiplierChange,
    onRemove,
    amountCursorKey,
    amountForceHover = false,
    amountForceFocus = false,
    multiplierCursorKey,
    multiplierForceHover = false,
    multiplierForceFocus = false
}: TrancheInputProps): React.ReactElement {
    return (
        <div className="flex items-center gap-2">
            <NumberField
                data-sim-cursor-key={amountCursorKey}
                forceHover={amountForceHover}
                forceFocus={amountForceFocus}
                value={amount}
                onChange={(n) => onAmountChange(n)}
                ariaLabel={amountLabel}
                placeholder="0"
                className="w-44"
                adornment={amountAdornment}
            />
            <div
                className={cn(
                    "flex items-center h-8 w-48 border border-line-base bg-transparent pl-2.5 pr-2",
                    // Forced demo hover/focus (sim): mirror NumberField's border
                    // cascade so the multiplier field darkens/focuses without a
                    // real pointer while the cursor "types" it. Inert in prod.
                    forcedFieldBorderClasses(
                        multiplierForceHover,
                        multiplierForceFocus
                    )
                )}
            >
                <MultiplierField
                    value={multiplier}
                    onChange={onMultiplierChange}
                    placeholder="1"
                    cursorKey={multiplierCursorKey}
                />
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-neutral shrink-0"
                >
                    x Multiplier
                </Text>
            </div>
            <IconButton onClick={onRemove} aria-label="Remove tranche">
                <CrossIcon className="size-4" />
            </IconButton>
        </div>
    );
}
